import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, FileSpreadsheet } from "lucide-react";
import { MESES_ABREV, formatCurrency } from "../../utils/orcamento";
import { adicionarAba, baixarWorkbook, criarWorkbook, tdStyle, thStyle } from "./DreGerencialView";

// DRE 2026 (ver pages/GestaoOrcamentaria/dreFechamento.js): tabela mensal do fechamento
// (realizado/orçado por mês). A comparação com o planejado do ano seguinte fica no Acumulado
// (YTD) da DRE Gerencial. O backend devolve as linhas já achatadas em ordem de árvore, com `nivel` e `temFilhos`.
// Custos e deduções vêm positivos (subtraídos na cascata), como no CSV de origem.

const LIMITE_PCT = 999; // margens sobre receita ~0 viram ruído — exibidas como "—"

function fmt(valor, unidade) {
  if (valor === null || valor === undefined) return "—";
  if (unidade === "pct") return Math.abs(valor) > LIMITE_PCT ? "—" : `${valor.toFixed(2)}%`;
  return formatCurrency(valor);
}

const BG_POR_TIPO = (C) => ({
  grupo: C.categoriaBg,
  subgrupo: C.subcategoriaBg,
  conta: "transparent",
  derivado: C.bgActive,
  margem: C.margemBg,
});

const PADDING_POR_NIVEL = [10, 24, 40];

function filtrarVisiveis(linhas, colapsados) {
  const visiveis = [];
  let ocultarAbaixoDe = null;
  for (const linha of linhas) {
    if (ocultarAbaixoDe !== null && linha.nivel > ocultarAbaixoDe) continue;
    ocultarAbaixoDe = null;
    visiveis.push(linha);
    if (linha.temFilhos && colapsados.has(linha.id)) ocultarAbaixoDe = linha.nivel;
  }
  return visiveis;
}

function CelulaLabel({ linha, colapsados, toggle, C }) {
  return (
    <td style={tdStyle(C, { textAlign: "left", position: "sticky", left: 0, background: "inherit", minWidth: 260 })}>
      <div
        onClick={linha.temFilhos ? () => toggle(linha.id) : undefined}
        style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: PADDING_POR_NIVEL[linha.nivel] || 10, cursor: linha.temFilhos ? "pointer" : "default" }}
      >
        {linha.temFilhos ? (colapsados.has(linha.id) ? <ChevronRight size={13} /> : <ChevronDown size={13} />) : <span style={{ width: 13 }} />}
        <span>{linha.label}</span>
      </div>
    </td>
  );
}

function estiloLinha(linha, C) {
  return {
    background: BG_POR_TIPO(C)[linha.tipo] || C.card,
    fontWeight: ["grupo", "derivado", "margem"].includes(linha.tipo) ? 700 : 400,
    fontStyle: linha.tipo === "margem" ? "italic" : "normal",
  };
}

function Card({ titulo, subtitulo, acao, children, C }) {
  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{titulo}</div>
          {subtitulo && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{subtitulo}</div>}
        </div>
        {acao}
      </div>
      <div style={{ overflowX: "auto" }}>{children}</div>
    </section>
  );
}

function KpiCard({ label, valor, sub, C }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 16px", minWidth: 170, flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800 }}>{valor}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function exportar(data) {
  const { ano, linhas, meses } = data;
  const wb = criarWorkbook();
  adicionarAba(wb, `DRE ${ano}`, linhas.map((l) => {
    const linha = { Linha: `${"  ".repeat(l.nivel)}${l.label}` };
    l.meses.forEach((v, i) => {
      const tipo = meses[i].tipo === "realizado" ? "Real." : meses[i].tipo === "orcado" ? "Orç." : "-";
      linha[`${MESES_ABREV[i]} (${tipo})`] = v ?? "-";
    });
    linha["Realizado YTD"] = l.totalRealizado ?? "-";
    linha[`Total ${ano}`] = l.total ?? "-";
    return linha;
  }));
  baixarWorkbook(wb, `DRE_${ano}`);
}

export default function DreFechamentoView({ data, C, carregando }) {
  const { ano, meses, linhas, realizadoAte, atualizadoEm, atualizadoPor } = data;
  const [colapsados, setColapsados] = useState(new Set());
  const toggle = (id) => setColapsados((s) => {
    const novo = new Set(s);
    novo.has(id) ? novo.delete(id) : novo.add(id);
    return novo;
  });
  useEffect(() => { setColapsados(new Set(["custos_backlog"])); }, [ano]);

  const semDados = meses.every((m) => m.tipo === null);
  const porId = Object.fromEntries(linhas.map((l) => [l.id, l]));
  const linhasMensais = filtrarVisiveis(linhas, colapsados);
  const periodoRealizado = realizadoAte ? `Jan–${MESES_ABREV[realizadoAte - 1]}/${ano}` : null;

  const botaoExportar = (
    <button
      type="button"
      onClick={() => exportar(data)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.text, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
    >
      <FileSpreadsheet size={13} /> Exportar Excel
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, opacity: carregando ? 0.6 : 1 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <KpiCard C={C} label={`Faturamento Bruto ${ano}`} valor={fmt(porId.faturamento_bruto?.total)} sub={`Realizado: ${fmt(porId.faturamento_bruto?.totalRealizado)}`} />
        <KpiCard C={C} label={`Lucro Bruto ${ano}`} valor={fmt(porId.lucro_bruto?.total)} sub={`Margem ${fmt(porId.margem_bruta?.total, "pct")}`} />
        <KpiCard C={C} label={`EBITDA ${ano}`} valor={fmt(porId.ebitda?.total)} sub={`Margem ${fmt(porId.margem_operacional?.total, "pct")}`} />
        <KpiCard C={C} label={`Resultado Líquido ${ano}`} valor={fmt(porId.resultado_liquido?.total)} sub={`Margem ${fmt(porId.margem_liquida?.total, "pct")}`} />
        <KpiCard
          C={C}
          label="Status"
          valor={periodoRealizado ? `Realizado ${periodoRealizado}` : "Sem realizado"}
          sub={atualizadoEm ? `Atualizado em ${new Date(atualizadoEm).toLocaleDateString("pt-BR")} · ${atualizadoPor || ""}` : "Nenhum CSV importado"}
        />
      </div>

      {semDados ? (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>
          Nenhum fechamento importado para {ano}.
        </div>
      ) : (
        <Card
          C={C}
          titulo={`DRE ${ano} — Fechamento mensal`}
          subtitulo={`Meses em verde = realizado; em amarelo = orçado (ainda não fechado). Custos e deduções positivos.`}
          acao={botaoExportar}
        >
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={thStyle(C, { textAlign: "left", position: "sticky", left: 0, background: C.card })}>Linha</th>
                {meses.map((m) => (
                  <th key={m.mes} style={thStyle(C, { background: m.tipo === "orcado" ? C.orcadoBg : undefined })}>
                    <div>{MESES_ABREV[m.mes - 1]}</div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: m.tipo === "realizado" ? C.success : m.tipo === "orcado" ? C.warning : C.muted }}>
                      {m.tipo === "realizado" ? "REAL." : m.tipo === "orcado" ? "ORÇ." : "—"}
                    </div>
                  </th>
                ))}
                <th style={thStyle(C, { borderLeft: `2px solid ${C.brand}` })}>Realizado<br />{periodoRealizado || ""}</th>
                <th style={thStyle(C)}>Total {ano}<br /><span style={{ fontSize: 9 }}>real. + orç.</span></th>
              </tr>
            </thead>
            <tbody>
              {linhasMensais.map((linha) => (
                <tr key={linha.id} style={estiloLinha(linha, C)}>
                  <CelulaLabel linha={linha} colapsados={colapsados} toggle={toggle} C={C} />
                  {linha.meses.map((v, i) => (
                    <td key={i} style={tdStyle(C, meses[i].tipo === "orcado" && linha.tipo === "conta" ? { background: C.orcadoBg } : {})}>
                      {fmt(v, linha.unidade)}
                    </td>
                  ))}
                  <td style={tdStyle(C, { borderLeft: `2px solid ${C.brand}`, fontWeight: 700 })}>{fmt(linha.totalRealizado, linha.unidade)}</td>
                  <td style={tdStyle(C, { fontWeight: 700 })}>{fmt(linha.total, linha.unidade)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

    </div>
  );
}
